// Import the QueryStatus type from the react-query package

import { AccountConfig } from '../accountConfig/accountConfig.interface';
import { Image } from '../media/media.interface';
import { Rebate } from '../rebate/rebate.interface';
import { User } from '../user/user.interface';

export type BugoMultiAgency<HeaderImage = Image, FooterImage = Image> = {
  headerImage?: HeaderImage; // 부고문자에 넣을 로고
  footerImage?: FooterImage; // 부고문자에 넣을 로고
  theme: string; // enum: default, dark, blue(프리드용)
  corpName: string;
  nickName: string;
  phoneNumber: string;
  address: string;
  frontUrl: string;
  regAlimTalkWebDomain: string;
  homepageUrl: string;
  kakaoAppJavascriptKey?: string;
  kakaoTemplateBugoButtonId?: string;
};

/** 정산 집계 기준일 */
export enum BugoBrandAccountDateType {
  PurchasedAt = 'purchasedAt', // 판매일 기준ㅇ
  CoffinOutDate = 'coffinOutDate', // 발인일 기준
}

// Define the BugoBrand type as an object with various properties
export type BugoBrandFeatureFlags = {
  giftEnabled?: boolean;
};

/** 답례품 리베이트 정산 방식 (giftAccountConfig.method) */
export enum GiftSettlementMethod {
  FeeRate = 'FeeRate', // 수수료율 차감 — 매출에서 수수료(feeRate %)만 떼고 나머지는 상조 귀속
  Rebate = 'Rebate', // 상품별 리베이트 — 상품별 priceRebate 값만 상조가 가져감
}

/** 답례품 정산 설정 (임베디드 — 계약 주체는 accountConfig.accountEntity를 따름) */
export type GiftAccountConfig = {
  enabled: boolean; // 답례품 정산 수행 여부
  viaMari: boolean; // 정산 시 마리 경유 여부
  method: GiftSettlementMethod; // 정산 방식 (기본 FeeRate)
  feeRate?: number; // method=FeeRate일 때 수수료율 (단위 %, 기본 5.3)
};

export type BugoBrand<
  Users = string[],
  Workers = undefined,
  ShopOrders = undefined,
  Shops = undefined,
  HeaderImage = string,
  FooterImage = string,
  Rebate = string,
  FuneralHomeInfo = string,
  FuneralHeaderImage = string,
  FuneralFooterImage = string,
  FuneralRebate = string,
> = {
  _id: string;
  users: Users;
  workers: Workers;
  type: string; // enum: BugoFuneralHome, BugoAgency
  shopOrders: ShopOrders;
  shops: Shops; // 협업하는 꽃집
  accountConfig?: AccountConfig | string; // 정산설정 (화환)
  giftAccountConfig?: GiftAccountConfig; // 답례품 정산설정 (임베디드)
  featureFlags?: BugoBrandFeatureFlags;
  bugoAgency?: // (nullable)
  {
    headerImage: HeaderImage; // 부고문자에 넣을 로고
    footerImage?: FooterImage; // 부고문자에 넣을 로고
    rebate: Rebate;
    theme: string; // enum: default, dark, blue(프리드용)
    corpName: string;
    nickName: string;
    phoneNumber: string;
    address: string;
    frontUrl: string;
    regAlimTalkWebDomain: string;
    regions: string[];
    teamTypes: string[];
    teamNames: string[];
    homepageUrl: string;
    kakaoAppJavascriptKey?: string;
    kakaoTemplateBugoButtonId?: string;
    shareRebateWithWorker?: boolean;
    accountDateType?: BugoBrandAccountDateType;
  };
  bugoAgencyList?: BugoMultiAgency<Image, Image>[]; // (nullable)
  bugoFuneralHome?: // (nullable)
  {
    funeralHomeInfo: FuneralHomeInfo; // 장례식장 물리적인 정보
    headerImage: FuneralHeaderImage; // 부고문자에 넣을 로고
    footerImage: FuneralFooterImage; // 부고문자에 넣을 로고
    rebate: FuneralRebate;
    theme: string; // enum: default, dark, blue(프리드용)
    homepageUrl: string;
  };
};

/**
 * 상조명 표기 전용 축약 타입.
 * fevent populate 시 select로 이름 필드만 잘라 받을 때 사용 (전체 문서는 설정/이미지까지 딸려와 무겁다)
 */
export type BugoBrandBrief = {
  _id: string;
  type?: string;
  bugoAgency?: {
    corpName?: string;
    nickName?: string;
  };
  bugoAgencyList?: {
    corpName?: string;
    nickName?: string;
  }[];
};

export type UserBugoBrand = BugoBrand<
  string[],
  undefined,
  undefined,
  undefined,
  Image,
  Image
>;

export type BugoBrandCreate = Omit<
  BugoBrand,
  '_id' | 'users' | 'workers' | 'shopOrders' | 'shops'
>;

export type BugoBrandUpdate<
  Users = string[],
  Workers = undefined,
  ShopOrders = undefined,
  Shops = undefined,
  HeaderImage = string,
  FooterImage = string,
  Rebate = string,
  FuneralHomeInfo = string,
  FuneralHeaderImage = string,
  FuneralFooterImage = string,
  FuneralRebate = string,
> = {
  _id: string;
  users?: Users;
  workers?: Workers;
  type?: string; // enum: BugoFuneralHome, BugoAgency
  shopOrders?: ShopOrders;
  shops?: Shops; // 협업하는 꽃집
  accountConfig?: string | null; // 정산설정 ID
  giftAccountConfig?: GiftAccountConfig; // 답례품 정산설정 (임베디드)
  featureFlags?: BugoBrandFeatureFlags;
  bugoAgency?: // (nullable)
  {
    headerImage?: HeaderImage; // 부고문자에 넣을 로고
    footerImage?: FooterImage; // 부고문자에 넣을 로고
    rebate?: Rebate;
    theme?: string; // enum: default, dark, blue(프리드용)
    corpName?: string;
    nickName?: string;
    phoneNumber?: string;
    address?: string;
    frontUrl?: string;
    regAlimTalkWebDomain?: string;
    regions?: string[];
    teamTypes?: string[];
    teamNames?: string[];
    homepageUrl?: string;
    kakaoAppJavascriptKey?: string;
    kakaoTemplateBugoButtonId?: string;
    shareRebateWithWorker?: boolean;
  };
  bugoFuneralHome?: // (nullable)
  {
    funeralHomeInfo?: FuneralHomeInfo; // 장례식장 물리적인 정보
    headerImage?: FuneralHeaderImage; // 부고문자에 넣을 로고
    footerImage?: FuneralFooterImage; // 부고문자에 넣을 로고
    rebate?: FuneralRebate;
    theme?: string; // enum: default, dark, blue(프리드용)
    homepageUrl?: string;
  };
};

export type BugoBrandData = BugoBrand<
  string[],
  User[],
  undefined,
  undefined,
  Image,
  Image,
  Rebate
>;

export enum BugoBrandType {
  BugoAgency = 'BugoAgency',
  BugoFuneralHome = 'BugoFuneralHome',
}
